import React, { memo, useState } from "react";
import { TableColumns } from "./Table";
import TableCell from "./TableCell";
import { X, Trash2, Edit3, Check } from "lucide-react";
import { EDSButton } from "../../eds/EDSButton";

type ActionAfterType = {
  disabledEdit?: boolean;
  deleteRow?: boolean;
};
export type EditButtonType<T> = {
  visible: boolean;
  onConfirm: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    row: T
  ) => Promise<ActionAfterType | void>;
  onCancel?: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    row: T
  ) => ActionAfterType | void;
};
export type DeleteButtonType<T> = {
  visible: boolean;
  onClick: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    row: T
  ) => Promise<ActionAfterType | void>;
};
type TableRowType<T> = {
  columns: TableColumns<T>[];
  row: T;
  y: number;
  editButton?: EditButtonType<T>;
  deleteButton?: DeleteButtonType<T>;
  newRowFlag?: keyof T;
  getAlignmentClass?: (align?: "Left" | "Center" | "Right") => string;
};

const TableRow = <T,>({
  columns,
  row,
  y,
  editButton,
  deleteButton,
  newRowFlag,
  getAlignmentClass
}: TableRowType<T>) => {
  const newRow = newRowFlag && row[newRowFlag];
  const [disabled, setDisabled] = useState<boolean>(!newRow);
  const [deleted, setDeleted] = useState<boolean>(false);

  const updateAfterAction = ({ disabledEdit, deleteRow }: ActionAfterType) => {
    if (disabledEdit !== undefined) {
      setDisabled(disabledEdit);
    }
    if (deleteRow !== undefined) {
      setDeleted(deleteRow);
    }
  };

  const isEditMode = !disabled;
  const rowClasses = [
    disabled ? "" : "Edit",
    deleted ? "hidden" : "",
    deleted ? "Deleted" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const paddingCellClasses = [
    "td-Padding",
    "w-panel-padding border-b-0",
    isEditMode ? "table-edit-row-cell" : ""
  ]
    .filter(Boolean)
    .join(" ");

  const buttonCellClasses = [
    "Table-RowButton-td",
    "border-b-0 w-[60px] py-2 px-3 text-center",
    isEditMode ? "table-edit-row-cell" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <tr className={rowClasses}>
      <td
        className={[
          paddingCellClasses,
          isEditMode ? "table-edit-row-cell-first" : ""
        ]
          .filter(Boolean)
          .join(" ")}
      />
      {columns.map((col, x) => (
        <TableCell
          key={`cell - ${x},${y}`}
          col={col}
          row={row}
          disabled={disabled}
          newRowFlag={newRowFlag}
          isEditMode={isEditMode}
          isFirstCell={x === 0}
          isLastCell={
            x === columns.length - 1 &&
            !editButton?.visible &&
            !deleteButton?.visible
          }
          getAlignmentClass={getAlignmentClass}
        />
      ))}
      {editButton?.visible && (
        <td className={buttonCellClasses}>
          <EDSButton
            variant={disabled ? "outline" : "default"}
            size="icon"
            className="mx-1"
            onClick={async (e) => {
              if (!disabled) {
                const response = await editButton.onConfirm(e, row);

                if (response) {
                  updateAfterAction(response);
                  return;
                }
              }

              setDisabled(!disabled);
            }}
          >
            {disabled ? <Edit3 size={16} /> : <Check size={16} />}
          </EDSButton>
        </td>
      )}
      {editButton?.visible && !disabled && (
        <td className={buttonCellClasses}>
          <EDSButton
            variant="outline"
            size="icon"
            className="mx-1"
            onClick={(e) => {
              const response = editButton.onCancel?.(e, row);
              if (response) {
                updateAfterAction(response);
                return;
              }

              setDisabled(!disabled);
            }}
          >
            <X size={16} />
          </EDSButton>
        </td>
      )}
      {deleteButton?.visible && disabled && (
        <td className={buttonCellClasses}>
          <EDSButton
            variant="outline"
            size="icon"
            className="mx-1"
            onClick={async (e) => {
              const response = await deleteButton.onClick(e, row);
              if (response) {
                updateAfterAction(response);
                return;
              }
            }}
          >
            <Trash2 size={16} />
          </EDSButton>
        </td>
      )}
      <td
        className={[
          paddingCellClasses,
          isEditMode ? "table-edit-row-cell-last" : ""
        ]
          .filter(Boolean)
          .join(" ")}
      />
    </tr>
  );
};

export default memo(TableRow) as typeof TableRow;
